import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Form, Grid } from 'components/base';
import { getTotalPrice } from 'data/lib/product';
import { formatPrice } from 'utils/format';
import useModalContext from 'components/context/modal';
import { animateScroll } from 'react-scroll';

import classNames from 'classnames';
import useHashQuery from 'ecommerce/hooks/useHash';
import {
  getEmptyPage,
  getOriginalPage,
  getTOCPages,
  hasPageLayers,
  getPageSize,
  PAGE_OPTION,
  getPagesByOptions,
} from 'editor-utils/template';
import toc from 'editor-utils/toc';
import { formatData } from 'editor-utils/dataParser';

import useEditorContext from '../../../context/editorContext';
import useEditorItemContext from '../../../context/editorItemContext';
import usePhotoWellContext from '../../../context/photoWellContext';
import usePhotoCropperContext from '../../../context/photoCropperContext';
import pagesValidator from '../../../lib/pagesValidator';

import PageWrapper from '../../components/pageWrapper';
import Page from '../../../components/page/preview';
import { Layout } from '../../../components/rich';
import useEditorRouter from '../../../lib/editorRouter';
import EditorLayout from '../../components/layout';
import { Button } from '../../../components/base';
import Icon, { ICON_TYPE } from '../../../components/base/icon';
import DesignSelect from '../../components/designSelect';
import OptionedContinueButton from '../../components/optionedContinueButton';
import TooltipPanel from '../../components/tooltipPanel';
import usePageSelectorContext from '../../../context/pageSelectorContext';
import withPolotno from '../../../hoc/withPolotno';
import useContentWizardContext from '../../../contentWizard/contentWizardContext';
import Thumbs from './thumbs';
import useAncestryFamilyTreeContext from '../../../context/ancestryFamilyTreeContext';
import withPageManager from '../../../hoc/withPageManager';

const BookComplexPreview = props => {
  const editorContext = useEditorContext();
  const modalContext = useModalContext();
  const contentWizardContext = useContentWizardContext();
  const { projectPersons } = useAncestryFamilyTreeContext();

  const movePageRef = useRef();

  const [foilCoverTextExceeds, setFoilCoverTextExceeds] = useState(false);

  // this magic has to be documented well
  const [viewPort, setViewPort] = useState([1200, 500]);
  let maxPageWidth = viewPort[0] / 2 - 20;
  let maxPageHeight = viewPort[1] - 86;
  let pageMenuPosition = 'bottom';
  let arrowSize = [140, 40, 50, 10];
  if (viewPort[1] < 420) {
    maxPageWidth -= 80;
    maxPageHeight += 38;
    pageMenuPosition = 'side';
    arrowSize = [80, 20, 28, 7];
  }

  const {
    item,
    product,
    template,
    params,
    config,
    alternativeProducts,
    numberOfPages,
  } = useEditorItemContext();
  const editorRouter = useEditorRouter(product.id);

  const photoWell = usePhotoWellContext();
  const photoCropper = usePhotoCropperContext();
  const pageSelector = usePageSelectorContext();

  const [cover, ...pages] = item.pages;
  const emptyPage = getEmptyPage(template);

  const personRelatedPageIds = useMemo(() => {
    return getPagesByOptions(template, [PAGE_OPTION.FAMILY_SHEET, PAGE_OPTION.TIMELINE]).map(
      page => page.id,
    );
  }, [template]);

  const [activePage, setActivePage] = useHashQuery('page', 'cover', val => {
    if (val === 'cover' || val === '' || val === 'NaN' || val == null || Number.isNaN(val)) {
      return 'cover';
    }
    try {
      return parseInt(val, 10);
    } catch (e) {
      return val;
    }
  });

  const activePageNumber = useMemo(() => {
    if (typeof activePage === 'number' && !Number.isNaN(activePage)) {
      return Math.min(Math.max(1, activePage), pages.length);
    }
    return undefined;
  }, [pages, activePage]);

  const handleOnSuccessClick = async (checkErrors = true) => {
    const validator = pagesValidator(template, item.pages, config, 'book');
    validator.confirmation(
      'I have carefully reviewed my project for errors and agree to print it “as is”. I understand that once I submit my order it will be sent to the printer and cannot be canceled. Because this is a custom product, it is not returnable for credit or refund.',
    );

    if (foilCoverTextExceeds) {
      if (hasPageLayers(getOriginalPage(template, item.pages[0], 'cover'))) {
        validator.error('Title exceeding the text box on the Cover Page.');
      }
    }

    const data = formatData(template, item, { validator });

    if (validator.isValid() || !checkErrors) {
      props.onSuccess({ data });
      return { data };
    }

    const errors = validator.getErrors();
    props.onSuccess({ errors });
    return { errors };
  };

  const addNewPage = (index, callback = () => { }) => {
    pageSelector.show(template, {
      title: 'Select New Page',
      pageNumber: index,
      onSelect: async page => {
        editorContext.addPages(item.id, [page], { index });
        callback({ ...page, index });
      },
    });
  };

  const handleOnPagePhotoUpload = (pageIndex, placeholder) => {
    photoWell.show({
      uploadEnabled: true,
      onSelectPhotos: selectedPhotos => {
        editorContext.addPhotoToPlaceholder(item.id, pageIndex, placeholder.id, selectedPhotos[0]);
        photoWell.hide();
      },
    });
  };

  const handleOnPagePhotoEdit = (pageIndex, placeholder, photo) => {
    photoCropper.show({
      placeholder,
      photo,
      onSuccess: data => {
        editorContext.setPhotoData(item.id, pageIndex, placeholder.id, data);
      },
    });
  };

  const handleOnPageTextChange = (pageIndex, placeholder, value, override) => {
    editorContext.setTextData(item.id, pageIndex, placeholder.id, value, override);
  };

  const handleOnChangeProduct = async newSku => {
    await editorRouter.changeProduct(newSku, template.version, '/preview');
    editorContext.changeProduct(item.id, product.id, newSku);
  };

  const handleOnPageAction = (pageIndex, action, actionData = {}) => {
    const originalPage = getOriginalPage(
      template,
      item.pages[pageIndex],
      pageIndex === 0 ? 'cover' : 'pages',
    );

    switch (action) {
      case 'edit':
        props.startPolotno(
          {
            pageKey: originalPage.key,
            onAddPage: addNewPage,
          },
          result => {
            editorContext.changePage(item.id, result.page, { pageKey: result.pageKey });
          },
          page => {
            setActivePage(page.index);
          },
        );
        break;

      case 'add':
        addNewPage(pageIndex);
        break;

      case 'clone':
        editorContext.duplicatePage(item.id, pageIndex);
        break;

      case 'delete':
        if (originalPage.isTOC) {
          modalContext.confirm('Do you really want to delete Table of Contents from your book?', {
            title: 'Delete Table of Contents?',
            onConfirm: () => {
              editorContext.removePageByIds(
                item.id,
                getTOCPages(template).map(page => page.id),
              );
            },
          });
        } else {
          modalContext.confirm('Do you really want to delete this page?', {
            title: 'Delete Page?',
            onConfirm: () => {
              editorContext.removePage(item.id, pageIndex);
            },
          });
        }

        break;

      case 'move':
        modalContext.confirm(
          <Grid.Row>
            <Grid.Col xs={8}>Select the page number you want to move this to:</Grid.Col>
            <Grid.Col xs={4}>
              <Form.Select ref={movePageRef} defaultValue={pageIndex}>
                {pages.map(page => (
                  <Form.SelectOption key={page.pageNumber} value={page.pageNumber}>
                    {page.pageLabel}
                  </Form.SelectOption>
                ))}
              </Form.Select>
            </Grid.Col>
          </Grid.Row>,
          {
            title: 'Move Page',
            confirmLabel: 'Move Page',
            onConfirm: () => {
              if (originalPage.isTOC) {
                const numbefOfTocPages = pages.filter(toc.isPage).length;
                editorContext.movePages(
                  item.id,
                  pageIndex,
                  numbefOfTocPages,
                  movePageRef.current.value,
                );
              } else {
                editorContext.movePage(item.id, false, pageIndex, movePageRef.current.value);
              }
            },
          },
        );
        break;

      case 'refreshToc':
        props.updateTOC();
        break;

      case 'records':
        contentWizardContext.openDialog({
          type: 'records',
          selectedPersons: actionData.persons,
          onAddPages: recordPages => {
            editorContext.addPages(item.id, recordPages, { index: pageIndex + 1 });
            contentWizardContext.closeDialog();
            try {
              gtag('event', 'add_records_done', {
                event_category: 'editor',
                event_label: recordPages.length,
              });
            } catch (e) { }
          },
        });

        try {
          gtag('event', 'add_records_init', {
            event_category: 'editor',
          });
        } catch (e) { }
        break;

      default:
        console.log({ pageIndex, action });
        break;
    }
  };

  const handleOnPageClick = (e, pageIndex) => {
    e.preventDefault();

    handleOnPageAction(pageIndex, 'edit');
  };

  const handleAddToBookClick = () => {
    contentWizardContext.openDialog({
      onNewPage: index => {
        setActivePage(index);
        const pageElement = document.getElementById(`page-${index}`);
        if (pageElement) {
          const offset = pageElement.offsetParent.offsetTop;

          animateScroll.scrollTo(offset - 15, {
            containerId: 'editor-container',
            smooth: true,
          });
        }
      },
    });
  };

  const activePages = useMemo(() => {
    const addLazyLoadingPage = ({ leftIndex, rightIndex } = {}) => {
      const getPages = () => {
        if (activePage === 'cover') {
          return pages.slice(0, 9);
        }
        if (activePage === 'back') {
          return pages.slice(pages.length % 2 === 0 ? -9 : -10);
        }
        if (leftIndex != null && leftIndex >= 0) {
          return pages.slice(Math.max(0, leftIndex - 4), leftIndex);
        }
        if (rightIndex != null) {
          return pages.slice(rightIndex + 1, Math.min(pages.length, rightIndex + 5));
        }
        return [];
      };

      return getPages().map(page => ({ page, visible: false }));
    };

    if (pages.length === 0) {
      return [];
    }

    if (typeof activePage === 'number' && !Number.isNaN(activePage)) {
      const index = Math.min(Math.max(1, activePage), pages.length) - 1;

      if (pages[index].span) {
        return [
          ...addLazyLoadingPage({ leftIndex: index }),
          {
            page: pages[index],
            index,
            visible: true,
          },
          ...addLazyLoadingPage({ rightIndex: index }),
        ];
      }

      let leftIndex = index - 1;
      let rightIndex = index;
      if (index % 2 === 1) {
        leftIndex = index;
        rightIndex = index + 1;
      }

      if (leftIndex < 0) {
        return [
          {
            page: pages[rightIndex],
            index: rightIndex,
            visible: true,
          },
          ...addLazyLoadingPage({ rightIndex }),
        ];
      }
      if (rightIndex >= pages.length) {
        return [
          ...addLazyLoadingPage({ leftIndex }),
          {
            page: pages[leftIndex],
            index: leftIndex,
            visible: true,
          },
        ];
      }

      return [
        ...addLazyLoadingPage({ leftIndex }),
        {
          page: pages[leftIndex],
          index: leftIndex,
          visible: true,
        },
        {
          page: pages[rightIndex],
          index: rightIndex,
          visible: true,
        },
        ...addLazyLoadingPage({ rightIndex }),
      ];
    }
    return [...addLazyLoadingPage({})];
  }, [pages, activePage]);

  const fullWidthCover = cover.spine != null;

  return (
    <>
      <EditorLayout
        onViewPortSizeChange={setViewPort}
        actionBar={
          <>
            {props.actionBar}
            {config.contentOptions != null && (
              <Button variant="secondary" onClick={handleAddToBookClick} mr={[1, 1, 2]}>
                <Icon type={ICON_TYPE.ADD} width={8} height={8} />
                Add to Book
              </Button>
            )}
          </>
        }
        numberOfPages={numberOfPages}
        actionButton={
          <OptionedContinueButton onClick={handleOnSuccessClick}>
            Add to Cart
          </OptionedContinueButton>
        }
        centerVertical
        footerChildren={
          <Thumbs
            pages={item.pages}
            template={template}
            photos={item.photos}
            config={config}
            onChangePage={setActivePage}
            activePage={activePage}
            activePages={activePages}
            onExpand={props.openPageManager}
          />
        }
        noPadding
        controls={
          <>
            {activePage !== 'cover' && (
              <div
                className="page-arrow"
                style={{
                  position: 'absolute',
                  left: -arrowSize[2],
                  top: (viewPort[1] - arrowSize[0]) / 2,
                  height: arrowSize[0],
                  background: '#FFFFFF44',
                  borderRadius: '100%',
                  border: `${arrowSize[3]}px solid #CCCCCC`,
                  padding: arrowSize[1],
                  zIndex: 12,
                }}
                onClick={() =>
                  setActivePage(val => {
                    if (val === 'back') {
                      return pages.length;
                    }
                    if (val === 1) {
                      if (template.cover) {
                        return 'cover';
                      }
                      return val;
                    }
                    const diff = activePages[0].page.span ? 1 : 2;
                    return Math.min(Math.max(1, val - diff), pages.length);
                  })
                }
              >
                <Icon
                  type={ICON_TYPE.ARROW2}
                  style={{ height: '100%', width: 'auto', color: '#CCCCCC' }}
                />
              </div>
            )}

            {activePage !== 'back' && (
              <div
                className="page-arrow"
                style={{
                  position: 'absolute',
                  right: -arrowSize[2],
                  top: (viewPort[1] - arrowSize[0]) / 2,
                  height: arrowSize[0],
                  background: '#FFFFFF44',
                  borderRadius: '100%',
                  border: `${arrowSize[3]}px solid #CCCCCC`,
                  padding: arrowSize[1],
                  zIndex: 12,
                }}
                onClick={() =>
                  setActivePage(val => {
                    if (val === 'cover') {
                      return 1;
                    }
                    if (val === pages.length) {
                      if (template.back && !fullWidthCover) {
                        return 'back';
                      }
                      return val;
                    }
                    const diff = activePages[0].page.span ? 1 : 2;
                    return Math.max(1, Math.min(val + diff, pages.length));
                  })
                }
              >
                <Icon
                  style={{ height: '100%', width: 'auto', color: '#CCCCCC' }}
                  type={ICON_TYPE.ARROW2}
                  rotate={180}
                />
              </div>
            )}
          </>
        }
      >
        <Layout.Container pt={0} pb={3} pl={0} pr={0}>
          <div className="text-center mx-auto">
            {activePage === 'cover' && template.cover && (
              <Grid.Row noGutters>
                {!fullWidthCover && <Grid.Col xs={6} className="col-page-preview" />}
                {alternativeProducts.length > 1 && (
                  <div
                    className="position-absolute text-left p-2"
                    style={{ left: 0, top: 0, maxWidth: 400, width: '50%' }}
                  >
                    <DesignSelect
                      title="Change Cover Type"
                      active={product.id}
                      onChange={handleOnChangeProduct}
                      items={alternativeProducts.map(alternative => ({
                        key: alternative.id,
                        title: alternative.templateMetaData.name,
                        subTitle:
                          product.price !== alternative.price
                            ? `${product.price > alternative.price ? '-' : '+'}${formatPrice(
                              Math.abs(product.price - alternative.price),
                              alternative.currency,
                            )}`
                            : null,
                        icon: `${alternative.templateRootPath}/assets_client/layouts/${cover.id}.png`,
                      }))}
                      className="mb-3"
                    />

                    <TooltipPanel key="tooltip" type="book-preview" />
                  </div>
                )}
                <Grid.Col
                  xs={fullWidthCover ? 12 : 6}
                  className={classNames({
                    'col-page-preview justify-content-start': true,
                    'align-items-start': !fullWidthCover,
                    'align-items-center': fullWidthCover,
                  })}
                >
                  {(() => {
                    const originalPage = getOriginalPage(template, cover, 'cover');

                    let coverPageEditable = false;
                    let coverPageProps = {
                      interactive: true,
                      onClick: undefined,
                      onTextExceeds: setFoilCoverTextExceeds,
                      page: originalPage,
                    };
                    if (!hasPageLayers(originalPage)) {
                      coverPageEditable = true;
                      coverPageProps = {
                        interactive: false,
                        onClick: e => handleOnPageClick(e, 0),
                        page: { ...cover, size: originalPage.size, type: 'cover', visible: true },
                        serverSideDPI: 72,
                      };
                    }

                    return (
                      <PageWrapper
                        id="page-0"
                        label="Cover Page"
                        buttonPosition="right"
                        menuPosition={pageMenuPosition}
                        onMenuItemClick={(action, actionData) =>
                          handleOnPageAction(0, action, actionData)
                        }
                        isCover
                        editable={coverPageEditable}
                        className={classNames({
                          'w-auto mx-unset': true,
                          'page-span-padding-left': pageMenuPosition === 'side' && fullWidthCover,
                        })}
                      >
                        <Page
                          photos={item.photos}
                          template={template}
                          onPhotoUpload={(...data) => handleOnPagePhotoUpload(0, ...data)}
                          onPhotoEdit={(...data) => handleOnPagePhotoEdit(0, ...data)}
                          onTextChange={(...data) => handleOnPageTextChange(0, ...data)}
                          fullWidth
                          {...coverPageProps}
                          className="mr-auto my-unset align-self-start"
                          style={{
                            maxWidth: fullWidthCover ? maxPageWidth * 2 : maxPageWidth,
                            maxHeight: maxPageHeight,
                          }}
                        />
                      </PageWrapper>
                    );
                  })()}
                </Grid.Col>
              </Grid.Row>
            )}

            <Grid.Row noGutters>
              {activePageNumber === 1 && !config.withoutFirstBlankPage && (
                <Grid.Col xs={6} className="col-page-preview justify-content-start align-items-end">
                  <PageWrapper noMenu className="w-auto mx-unset">
                    <Page
                      page={emptyPage}
                      fullWidth
                      style={{ maxWidth: maxPageWidth, maxHeight: maxPageHeight }}
                    />
                  </PageWrapper>
                </Grid.Col>
              )}

              {activePages.map(({ page, index, visible }) => {
                const type = 'pages';
                const [width, height] = getPageSize(template, type, page.span);

                let leftPage;
                let buttonPosition;
                if (page.span) {
                  buttonPosition = 'right';
                } else if (index % 2 === 1) {
                  leftPage = true;
                  buttonPosition = 'left';
                } else if (index % 2 === 0) {
                  leftPage = false;
                  buttonPosition = 'right';
                }

                let persons;
                if (personRelatedPageIds.indexOf(page.id) >= 0) {
                  persons = (projectPersons ?? []).filter(
                    p => (page.tags ?? []).indexOf(p.id) >= 0 && p.hasCitations,
                  );
                }

                return (
                  <React.Fragment key={page.key}>
                    <Grid.Col
                      xs={page.span ? 12 : 6}
                      className={classNames({
                        'col-page-preview justify-content-start': true,
                        'd-none': visible === false,
                        'align-items-end': leftPage === true,
                        'align-items-start': leftPage === false,
                        'align-items-center': page.span,
                      })}
                    >
                      <PageWrapper
                        id={`page-${page.pageNumber}`}
                        label={page.span ? page.pageLabel : `Page ${page.pageNumber}`}
                        buttonPosition={buttonPosition}
                        menuPosition={pageMenuPosition}
                        onMenuItemClick={(action, actionData) =>
                          handleOnPageAction(index + 1, action, actionData)
                        }
                        isTOCPage={toc.isPage(page)}
                        isTOCFirstPage={toc.isFirstPage(page, pages)}
                        persons={persons}
                        className={classNames({
                          'w-auto mx-unset': true,
                          'page-span-padding-left': pageMenuPosition === 'side' && page.span,
                        })}
                      >
                        <Page
                          page={{ ...page, size: { width, height }, type, visible }}
                          photos={item.photos}
                          template={template}
                          onPhotoUpload={(...data) => handleOnPagePhotoUpload(index + 1, ...data)}
                          onPhotoEdit={(...data) => handleOnPagePhotoEdit(index + 1, ...data)}
                          onTextChange={(...data) => handleOnPageTextChange(index + 1, ...data)}
                          fullWidth
                          onClick={e => handleOnPageClick(e, index + 1)}
                          serverSideDPI={72}
                          visibleByDefault
                          withImageMagnify={visible && false}
                          className={classNames({
                            'my-unset mx-unset': true,
                            'align-self-end': leftPage === true,
                            'align-self-start': leftPage === false || page.span,
                          })}
                          style={{
                            maxWidth: page.span ? maxPageWidth * 2 : maxPageWidth,
                            maxHeight: maxPageHeight,
                          }}
                        />
                      </PageWrapper>
                    </Grid.Col>
                  </React.Fragment>
                );
              })}

              {activePageNumber === pages.length &&
                activePageNumber % 2 === 0 &&
                !config.withoutFirstBlankPage && (
                  <Grid.Col
                    xs={6}
                    className="col-page-preview justify-content-start align-items-start"
                  >
                    <PageWrapper noMenu className="w-auto mx-unset">
                      <Page
                        page={emptyPage}
                        style={{ maxWidth: maxPageWidth, maxHeight: maxPageHeight }}
                      />
                    </PageWrapper>
                  </Grid.Col>
                )}
            </Grid.Row>

            {activePage === 'back' && template.back && !fullWidthCover && (
              <Grid.Row noGutters>
                <Grid.Col xs={6} className="col-page-preview justify-content-start align-items-end">
                  {(() => {
                    const originalPage = getOriginalPage(template, {}, 'back');

                    return (
                      <PageWrapper
                        id={`page-${item.pages.length + (pages.length % 2 ? 0 : 1)}`}
                        editable={false}
                        buttonPosition="left"
                        menuPosition={pageMenuPosition}
                        label="Back Cover"
                        className="w-auto mx-unset"
                      >
                        <Page
                          page={originalPage}
                          template={template}
                          fullWidth
                          style={{ maxWidth: maxPageWidth, maxHeight: maxPageHeight }}
                        />
                      </PageWrapper>
                    );
                  })()}
                </Grid.Col>
                <Grid.Col xs={6} className="col-page-preview" />
              </Grid.Row>
            )}
          </div>
        </Layout.Container>
      </EditorLayout>
    </>
  );
};

export default withPageManager(withPolotno(BookComplexPreview));
